import React, { useState, useEffect } from 'react';
import type {
  DoctorProfile,
  Specialization,
  Subspecialization,
  MedicalCouncil,
  CouncilInfo,
} from '../../../types/doctorOnboarding';

type Props = {
  profile: DoctorProfile | null;
  specializations: Specialization[];
  subspecializations: Subspecialization[];
  councilInfo: CouncilInfo | null;
  allMedicalCouncils: MedicalCouncil[];
  onLoadSubspecializations: (specializationId: number) => Promise<void>;
  onLoadCouncilInfo: (specializationId: number) => Promise<void>;
  onSave: (professional: Partial<DoctorProfile>) => Promise<void>;
  saving: boolean;
  onBack: () => void;
};

const currentYear = new Date().getFullYear();
const regNumberRegex = /^[A-Za-z0-9\s/-]+$/;

export default function ProfessionalInfoStep({
  profile,
  specializations,
  subspecializations,
  councilInfo,
  allMedicalCouncils,
  onLoadSubspecializations,
  onLoadCouncilInfo,
  onSave,
  saving,
  onBack,
}: Props) {
  const [specializationId, setSpecializationId] = useState<number | ''>(
    profile?.specialization_id ?? ''
  );
  const [subspecializationId, setSubspecializationId] = useState<number | ''>(
    profile?.subspecialization_id ?? ''
  );
  const [registrationNumber, setRegistrationNumber] = useState(
    profile?.registration_number ?? ''
  );
  const [registrationCouncil, setRegistrationCouncil] = useState(
    profile?.registration_council ?? ''
  );
  const [registrationYear, setRegistrationYear] = useState<number | ''>(
    profile?.registration_year ?? ''
  );
  const [yearsExperience, setYearsExperience] = useState(
    profile?.years_experience ?? 0
  );

  useEffect(() => {
    if (!profile) return;
    setSpecializationId(profile.specialization_id ?? '');
    setSubspecializationId(profile.subspecialization_id ?? '');
    setRegistrationNumber(profile.registration_number ?? '');
    setRegistrationCouncil(profile.registration_council ?? '');
    setRegistrationYear(profile.registration_year ?? '');
    setYearsExperience(profile.years_experience ?? 0);
  }, [profile]);

  useEffect(() => {
    if (specializationId === '') return;
    onLoadSubspecializations(Number(specializationId));
    onLoadCouncilInfo(Number(specializationId));
    setSubspecializationId('');
  }, [specializationId, onLoadSubspecializations, onLoadCouncilInfo]);

  const registrationRequired =
    councilInfo?.registration_required === 'mandatory' ||
    councilInfo?.registration_required === 'specific_roles';
  const councilInfoReady = specializationId === '' || !!councilInfo;
  const regTrimmed = registrationNumber.trim();
  const regNumberValid =
    regTrimmed.length >= 3 &&
    regTrimmed.length <= 50 &&
    regNumberRegex.test(regTrimmed);
  const regCouncilValid = registrationCouncil.trim().length > 0;
  const regYearValid =
    registrationYear !== '' &&
    Number(registrationYear) >= 1950 &&
    Number(registrationYear) <= currentYear;
  const specializationValid = specializationId !== '';
  const whenRegistrationRequired =
    !registrationRequired || (regNumberValid && regCouncilValid && regYearValid);
  const isValid =
    specializationValid &&
    councilInfoReady &&
    yearsExperience >= 0 &&
    yearsExperience <= 60 &&
    whenRegistrationRequired;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;
    onSave({
      specialization_id:
        typeof specializationId === 'string' ? undefined : specializationId,
      subspecialization_id:
        typeof subspecializationId === 'string' ? undefined : subspecializationId,
      registration_number: regTrimmed || undefined,
      registration_council: registrationCouncil.trim() || undefined,
      registration_year:
        registrationYear === '' ? undefined : Number(registrationYear),
      years_experience: yearsExperience,
    });
  };

  const activeSpecializations = specializations.filter((s) => s.is_active);
  const activeSubs = subspecializations.filter((s) => s.is_active);
  const activeCouncils = allMedicalCouncils.filter((c) => c.is_active);

  return (
    <form onSubmit={handleSubmit}>
      <div className="onboarding-section">
        <h3>Professional Credentials</h3>
        <div className="form-group">
          <label>Primary Specialization <span className="required">*</span></label>
          <select
            value={specializationId === '' ? '' : specializationId}
            onChange={(e) =>
              setSpecializationId(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
          >
            <option value="">Select specialization</option>
            {activeSpecializations.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {!specializationValid && (
            <span className="field-hint">Please select a specialization</span>
          )}
          {specializationValid && !councilInfoReady && (
            <span className="field-hint">Loading council requirements...</span>
          )}
        </div>
        <div className="form-group">
          <label>Sub-specialization</label>
          <select
            value={subspecializationId === '' ? '' : subspecializationId}
            onChange={(e) =>
              setSubspecializationId(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
            disabled={!specializationId}
          >
            <option value="">Select (optional)</option>
            {activeSubs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Years of Experience <span className="required">*</span></label>
          <input
            type="number"
            min={0}
            max={60}
            value={yearsExperience}
            onChange={(e) => setYearsExperience(parseInt(e.target.value, 10) || 0)}
          />
        </div>

        {(registrationRequired || councilInfo) && (
          <>
            <div className="form-group">
              <label>
                Registration Council{' '}
                {registrationRequired && <span className="required">*</span>}
              </label>
              <select
                value={registrationCouncil}
                onChange={(e) => setRegistrationCouncil(e.target.value)}
              >
                <option value="">Select council</option>
                {activeCouncils.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Registration Number{' '}
                {registrationRequired && <span className="required">*</span>}
              </label>
              <input
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                placeholder="MCI/NMC number"
                maxLength={50}
              />
              {registrationRequired && regTrimmed && !regNumberValid && (
                <span className="field-hint">
                  3–50 characters; letters, numbers, spaces, hyphens, slashes only
                </span>
              )}
            </div>
            <div className="form-group">
              <label>
                Registration Year{' '}
                {registrationRequired && <span className="required">*</span>}
              </label>
              <input
                type="number"
                min={1950}
                max={currentYear}
                value={registrationYear === '' ? '' : registrationYear}
                onChange={(e) =>
                  setRegistrationYear(
                    e.target.value === '' ? '' : parseInt(e.target.value, 10)
                  )
                }
                placeholder={String(currentYear)}
              />
              {registrationRequired &&
                registrationYear !== '' &&
                !regYearValid && (
                  <span className="field-hint">
                    Year must be 1950–{currentYear}
                  </span>
                )}
            </div>
          </>
        )}
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="submit" className="btn-primary" disabled={!isValid || saving}>
          {saving ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </form>
  );
}
